import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ChangeDetectorRef,
} from "@angular/core";
import { marker as _ } from "@biesbjerg/ngx-translate-extract-marker";
import {
  Asset,
  AssetPickerDialogComponent,
  CancelOrderInput,
  DataService,
  Dialog,
  getAppConfig,
  I18nService,
  ModalService,
  OrderDetailFragment,
  OrderLineInput,
  PaymentWithRefundsFragment,
  RefundOrderInput,
  SharedModule,
} from "@bb-vendure/admin-ui/core";
import { unique } from "@vendure/common/lib/unique";
import { summate } from "@vendure/common/lib/shared-utils";
import {
  getRefundablePayments,
  RefundablePayment,
} from "@bb-vendure/admin-ui/order";
import { LineRefundsComponent } from "../line-refunds/line-refunds.component";
import { PaymentForRefundSelectorComponent } from "../payment-for-refund-selector/payment-for-refund-selector.component";
import { take } from "rxjs";
import { CreateRefundRequestDocument } from "../generated-graphql/graphql";

type SelectionLine = { quantity: number; cancel: boolean };

@Component({
  standalone: true,
  imports: [
    SharedModule,
    LineRefundsComponent,
    PaymentForRefundSelectorComponent,
  ],
  selector: "vdr-refund-order-dialog",
  templateUrl: "./refund-order-dialog.component.html",
  styleUrls: ["./refund-order-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RefundOrderDialogComponent
  implements
    OnInit,
    Dialog<boolean>
{
  order: OrderDetailFragment;
  files: Asset[] = [];
  resolveWith: (result?: boolean) => void;
  reason: string;
  lineQuantities: { [lineId: string]: SelectionLine } = {};
  refundablePayments: RefundablePayment[] = [];
  refundShippingLineIds: string[] = [];
  reasons = getAppConfig().cancellationReasons ?? [
    _("order.refund-reason-customer-request"),
    _("order.refund-reason-not-available"),
  ];
  manuallySetRefundTotal = false;
  refundTotal = 0;

  constructor(
    private i18nService: I18nService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
    private dataService: DataService
  ) {
    this.reasons = this.reasons.map((r) => this.i18nService.translate(r));
  }

  get totalRefundableAmount(): number {
    return summate(this.refundablePayments, "refundableAmount");
  }

  get amountToRefundTotal(): number {
    return this.refundablePayments.reduce(
      (total, payment) => total + payment.amountToRefundControl.value,
      0
    );
  }

  lineCanBeRefundedOrCancelled(
    line: OrderDetailFragment["lines"][number]
  ): boolean {
    const refundedCount =
      this.order.payments
        ?.reduce(
          (all, payment) => [...all, ...payment.refunds],
          [] as PaymentWithRefundsFragment["refunds"]
        )
        .filter((refund) => refund.state !== "Failed")
        .reduce(
          (all, refund) => [...all, ...refund.lines],
          [] as Array<{ orderLineId: string; quantity: number }>
        )
        .filter((refundLine) => refundLine.orderLineId === line.id)
        .reduce((sum, refundLine) => sum + refundLine.quantity, 0) ?? 0;

    return refundedCount < line.orderPlacedQuantity;
  }

  ngOnInit() {
    this.lineQuantities = this.order.lines.reduce(
      (result, line) => ({
        ...result,
        [line.id]: {
          quantity: 0,
          refund: true,
          cancel: false,
        },
      }),
      {}
    );
    this.refundablePayments = getRefundablePayments(this.order.payments);
  }

  updateRefundTotal() {
    if (!this.manuallySetRefundTotal) {
      const itemTotal = this.order.lines.reduce((total, line) => {
        const lineRef = this.lineQuantities[line.id];
        const refundCount = lineRef.quantity || 0;
        return total + line.proratedUnitPriceWithTax * refundCount;
      }, 0);
      const shippingTotal = this.order.shippingLines.reduce((total, line) => {
        if (this.refundShippingLineIds.includes(line.id)) {
          return total + line.discountedPriceWithTax;
        } else {
          return total;
        }
      }, 0);
      this.refundTotal = itemTotal + shippingTotal;
    }

    // allocate the refund total across the refundable payments
    const refundablePayments = this.refundablePayments.filter(
      (p) => p.selected
    );
    let refundsAllocated = 0;
    for (const payment of refundablePayments) {
      const amountToRefund = Math.min(
        payment.refundableAmount,
        this.refundTotal - refundsAllocated
      );
      payment.amountToRefundControl.setValue(amountToRefund);
      refundsAllocated += amountToRefund;
    }
  }

  toggleShippingRefund(lineId: string) {
    const index = this.refundShippingLineIds.indexOf(lineId);
    if (index === -1) {
      this.refundShippingLineIds.push(lineId);
    } else {
      this.refundShippingLineIds.splice(index, 1);
    }
    this.updateRefundTotal();
  }

  onRefundQuantityChange(orderLineId: string, quantity: number) {
    this.manuallySetRefundTotal = false;
    const selectionLine = this.lineQuantities[orderLineId];
    if (selectionLine) {
      const previousQuantity = selectionLine.quantity;
      if (quantity === 0) {
        selectionLine.cancel = false;
      } else if (previousQuantity === 0 && quantity > 0) {
        selectionLine.cancel = true;
      }
      selectionLine.quantity = quantity;
      this.updateRefundTotal();
    }
  }

  onPaymentSelected(payment: RefundablePayment, selected: boolean) {
    if (selected) {
      const outstandingRefundAmount =
        this.refundTotal -
        this.refundablePayments
          .filter((p) => p.id !== payment.id)
          .reduce((total, p) => total + p.amountToRefundControl.value, 0);
      if (0 < outstandingRefundAmount) {
        payment.amountToRefundControl.setValue(
          Math.min(outstandingRefundAmount, payment.refundableAmount)
        );
      }
    } else {
      payment.amountToRefundControl.setValue(0);
    }
  }

  isRefunding(): boolean {
    const result = Object.values(this.lineQuantities).reduce(
      (isRefunding, line) => isRefunding || 0 < line.quantity,
      false
    );
    return result;
  }

  isCancelling(): boolean {
    const result = Object.values(this.lineQuantities).reduce(
      (isCancelling, line) =>
        isCancelling || (0 < line.quantity && line.cancel),
      false
    );
    return result;
  }

  canSubmit(): boolean {
    return (
      0 < this.refundTotal &&
      this.amountToRefundTotal === this.refundTotal &&
      !!this.reason
    );
  }

  selectAllRefundAndCancel(): void {
    // 1. 遍历所有订单行，自动填充最大可退款数量 + 勾选返回库存
    this.order.lines.forEach(line => {
      if (this.lineCanBeRefundedOrCancelled(line)) {
        const lineQty = this.lineQuantities[line.id];
        if (lineQty) {
          this.lineQuantities[line.id] = {
            ...lineQty,
            quantity: line.quantity,
            cancel: true,
          };
        }
      }
    });

    // 3. 自动填写退款原因：用户请求
    this.reason = '用户请求';

    this.order.shippingLines.forEach(sl => {
      const checkbox = document.getElementById(`shipping-${sl.id}`) as HTMLInputElement;
      if (checkbox) {
        if (!checkbox.checked){
          checkbox.checked = true;
          this.toggleShippingRefund(sl.id); 
        }
      }
    });

    this.cdr.markForCheck();
  }

  select() {
    const refundLines = this.getOrderLineInput(() => true);
    const cancelLines = this.getOrderLineInput((line) => line.cancel);

    // this.resolveWith({
    //   refunds: this.refundablePayments
    //     .filter((rp) => rp.selected && 0 < rp.amountToRefundControl.value)
    //     .map((payment) => {
    //       return {
    //         lines: refundLines,
    //         reason: this.reason,
    //         paymentId: payment.id,
    //         amount: payment.amountToRefundControl.value,
    //         shipping: 0,
    //         adjustment: 0,
    //       };
    //     }),
    //   cancel: {
    //     lines: cancelLines,
    //     orderId: this.order.id,
    //     reason: this.reason,
    //     cancelShipping: this.refundShippingLineIds.length > 0,
    //   },
    // });

    const results = {
      refunds: this.refundablePayments
        .filter((rp) => rp.selected && 0 < rp.amountToRefundControl.value)
        .map((payment) => {
          return {
            lines: refundLines,
            reason: this.reason,
            paymentId: payment.id,
            amount: payment.amountToRefundControl.value,
            shipping: 0,
            adjustment: 0,
          };
        }),
      cancel: {
        lines: cancelLines,
        orderId: this.order.id,
        reason: this.reason,
        cancelShipping: this.refundShippingLineIds.length > 0,
      },
    };


    
    

    this.dataService
      .mutate(CreateRefundRequestDocument, {
        input: {
          lines: this.order.lines.map((l) => ({
            orderLineId: l.id,
            quantity: l.quantity,
          })),
          paymentId: this.order!.payments![0].id,
          reason: this.reason,
          status: "new",
          assetIds:
            this.files.length > 0 ? this.files.map((f) => f.id) : undefined,
          args: results,
        },
      })
      .subscribe(({ createRefundRequest }) => {
        if (createRefundRequest.id) {
          this.resolveWith(true);
        }
      });
  }

  cancel() {
    this.resolveWith(false);
  }

  private getOrderLineInput(
    filterFn: (line: SelectionLine) => boolean
  ): OrderLineInput[] {
    return Object.entries(this.lineQuantities)
      .filter(([orderLineId, line]) => 0 < line.quantity && filterFn(line))
      .map(([orderLineId, line]) => ({
        orderLineId,
        quantity: line.quantity,
      }));
  }

  addFile() {
    this.modalService
      .fromComponent(AssetPickerDialogComponent, {
        size: "xl",
      })
      .pipe(take(1))
      .subscribe((result) => {
        console.log(result);

        if (result && result.length) {
          this.files = unique(this.files.concat(result), "id");
          this.cdr.detectChanges();
        }
      });
  }

  remove(id: string) {
    const newAssets = this.files.filter((a) => a.id !== id);
    this.files = newAssets;
  }
}
